// core.service.ts
import { Injectable, EventEmitter } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import * as Cesium from 'cesium';
type ListenerFunction = (...args: any[]) => void;

@Injectable({
  providedIn: 'root',
})
export class CoreService {
  private cesiumViewer: any;
  private leafletViewer: any;
  private _globeClickHandler: any;
  selectionIndicatorChanged = new EventEmitter<any>();
  
  private globeClickListeners: Record<string, ListenerFunction> = {};
  private onTickListeners: Record<string, ListenerFunction> = {};
  private timeupdateListeners: Record<string, ListenerFunction> = {};
  private morphCompleteListeners: Record<string, ListenerFunction> = {};
  private morphStartListeners: Record<string, ListenerFunction> = {};

  private selectedEntity: any;

  // Add this for tool activation
  private toolActiveSubject = new BehaviorSubject<boolean>(false);
  toolActive$ = this.toolActiveSubject.asObservable();

  setToolActive(isActive: boolean) {
    this.toolActiveSubject.next(isActive);
  }

  // Timeline methods
  setCurrentTimestamp(timeInMilliseconds: number): void {
    if (this.cesiumViewer) {
      this.cesiumViewer.clock.currentTime = Cesium.JulianDate.fromDate(new Date(timeInMilliseconds));
      this.fireOnUpdateTime(this.cesiumViewer.clock.currentTime);
    }
  }

  getCurrentTime(): number | undefined {
    if (!this.cesiumViewer) return undefined;
    return Cesium.JulianDate.toDate(this.cesiumViewer.clock.currentTime).getTime();
  }

  getCurrentTimestamp(): Date | undefined {
    if (!this.cesiumViewer) return undefined;
    return Cesium.JulianDate.toDate(this.cesiumViewer.clock.currentTime);
  }

  getCurrentJulianTime(): any | undefined {
    return this.cesiumViewer?.clock.currentTime;
  }

  pauseTime(): void {
    if (this.cesiumViewer) this.cesiumViewer.clock.shouldAnimate = false;
  }

  startTime(): void {
    if (this.cesiumViewer) this.cesiumViewer.clock.shouldAnimate = true;
  }

  // Globe event firing methods
  fireGlobeClickEventHandlers(lat: number, lon: number, height: number): void {
    Object.values(this.globeClickListeners).forEach((listener) => {
      listener(lat, lon, height);
    });
  }

  fireOnTickEventHandlers(clock: any, currentTime: any): void {
    Object.values(this.onTickListeners).forEach((listener) => {
      listener(clock, currentTime);
    });
  }

  fireOnUpdateTime(currentTime: any): void {
    Object.values(this.timeupdateListeners).forEach((listener) => {
      listener(currentTime);
    });
  }

  fireMorphStartListeners(prevMode: any, newMode: any): void {
    Object.values(this.morphStartListeners).forEach((listener) => {
      listener(prevMode, newMode);
    });
  }

  fireMorphCompleteListeners(prevMode: any, newMode: any): void {
    Object.values(this.morphCompleteListeners).forEach((listener) => {
      listener(prevMode, newMode);
    });
  }

  // Viewer setters and getters
  setViewer(viewer: any): void {
    this.cesiumViewer = viewer;
    if (!this._globeClickHandler) {
      this._globeClickHandler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
      this._globeClickHandler.setInputAction((movement: any) => {
        
        let cartesian;
        let cartographic;
        
        // First try to pick a position on 3D models/buildings
        cartesian = viewer.scene.pickPosition(movement.position);
        
        if (!cartesian) {
          // If pickPosition fails, try picking on the globe/terrain
          const ray = viewer.scene.camera.getPickRay(movement.position);
          if (ray) {
            cartesian = viewer.scene.globe.pick(ray, viewer.scene);
          }
        }
        
        if (!cartesian) {
          // If both methods fail, use camera ray intersection with ellipsoid as fallback
          const ray = viewer.scene.camera.getPickRay(movement.position);
          if (ray) {
            cartesian = Cesium.IntersectionTests.rayEllipsoid(ray, viewer.scene.globe.ellipsoid);
          }
        }
        
        if (!cartesian) {
          return;
        }
        
        cartographic = Cesium.Cartographic.fromCartesian(cartesian);
        const lat = Cesium.Math.toDegrees(cartographic.latitude);
        const lon = Cesium.Math.toDegrees(cartographic.longitude);
        
        Object.values(this.globeClickListeners).forEach((listener) => {
          listener(lat, lon);
        });
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
    }
  }

  getViewer(): any {
    return this.cesiumViewer;
  }

  setLeafletViewer(viewer: any): void {
    this.leafletViewer = viewer;
  }

  getLeafletViewer(): any {
    return this.leafletViewer;
  }

  getScene(): any {
    return this.cesiumViewer?.scene;
  }

  // Listener management methods
  addGlobeClickListener(objectKey: string, listener: ListenerFunction): void {
    this.globeClickListeners[objectKey] = listener;
  }
  removeGlobeClickListener(objectKey: string): void {
    delete this.globeClickListeners[objectKey];
  }

  // addOnTickListener(objectKey: string, listener: ListenerFunction): void {
  //   this.onTickListeners[objectKey] = listener;
  // }
  addOnTickListener(key: string, listener: any): void {
    this.onTickListeners[key] = listener;
  }
  removeOnTickListener(objectKey: string): void {
    delete this.onTickListeners[objectKey];
  }

  addTimeUpdateListener(objectKey: string, listener: ListenerFunction): void {
    this.timeupdateListeners[objectKey] = listener;
  }
  removeTimeUpdateListener(objectKey: string): void {
    delete this.timeupdateListeners[objectKey];
  }

  addMorphStartListener(objectKey: string, listener: ListenerFunction): void {
    this.morphStartListeners[objectKey] = listener;
  }
  removeMorphStartListener(objectKey: string): void {
    delete this.morphStartListeners[objectKey];
  }

  addMorphCompleteListener(objectKey: string, listener: ListenerFunction): void {
    this.morphCompleteListeners[objectKey] = listener;
  }
  removeMorphCompleteListener(objectKey: string): void {
    delete this.morphCompleteListeners[objectKey];
  }

  // Selected entity methods
  getSelectedEntity(): any {
    return this.selectedEntity;
  }

  setSelectedEntity(entity: any): void {
    if (this.selectedEntity && typeof this.selectedEntity.setIsTargeted === 'function') {
      this.selectedEntity.setIsTargeted(false);
    }
    this.selectedEntity = entity;
  }

  clearSelectedEntity(): void {
    this.selectedEntity = undefined;
  }

  // Tracking entity methods
  getTrackingEntity(): any {
    return this.cesiumViewer?.trackedEntity;
  }

  setTrackingEntity(arcLiveEntity: any): void {
    if (arcLiveEntity && this.cesiumViewer) {
      this.cesiumViewer.trackedEntity = arcLiveEntity.getCesiumEntity();
    }
  }

  clearTrackingEntity(): void {
    if (this.cesiumViewer) {
      this.cesiumViewer.trackedEntity = undefined;
    }
  }

  // Scene mode methods
  setSceneModeTo2d(duration?: number): void {
    const morphDuration = duration ?? 0;
    this.cesiumViewer?.scene.morphTo3D(morphDuration);
  }

  setSceneModeTo3d(duration?: number): void {
    const morphDuration = duration ?? 0;
    this.cesiumViewer?.scene.morphTo3D(morphDuration);
  }

  getSceneMode(): any {
    return this.cesiumViewer?.scene.mode;
  }

  isIn2DMode(): boolean {
    return this.cesiumViewer?.scene.mode === Cesium.SceneMode.SCENE3D;
  }

  isIn3DMode(): boolean {
    return this.cesiumViewer?.scene.mode === Cesium.SceneMode.SCENE3D;
  }


  // Get center coordinate method
  getCenterCoordinate(): { lat: number; long: number } {
    if (!this.cesiumViewer) {
      return { lat: 0, long: 0 };
    }

    let pickPositionCartographic: any;

    const sceneMode = this.cesiumViewer.scene.mode;

    if (sceneMode === Cesium.SceneMode.SCENE2D) {
      pickPositionCartographic = this.cesiumViewer.camera.positionCartographic;
      if (!pickPositionCartographic) {
        return { lat: 0, long: 0 };
      }
    } else if (sceneMode === Cesium.SceneMode.SCENE3D) {
      const windowPosition = new Cesium.Cartesian2(
        this.cesiumViewer.container.clientWidth / 2,
        this.cesiumViewer.container.clientHeight / 2
      );
      const pickRay = this.cesiumViewer.scene.camera.getPickRay(windowPosition);
      const pickPosition = this.cesiumViewer.scene.globe.pick(pickRay, this.cesiumViewer.scene);

      if (pickPosition) {
        pickPositionCartographic = this.cesiumViewer.scene.globe.ellipsoid.cartesianToCartographic(pickPosition);
      } else {
        return { lat: 0, long: 0 };
      }
    } else {
      return { lat: 0, long: 0 };
    }

    return {
      lat: pickPositionCartographic.latitude * (180 / Math.PI),
      long: pickPositionCartographic.longitude * (180 / Math.PI),
    };
  }
}
